import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, MessageCircle, ExternalLink } from "lucide-react";

import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FacebookIcon, InstagramIcon } from "../MetaIcons";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { buscarAppConfigMeta, conectarPelaMeta } from "../../helpers/metaLogin";

// Conectar um canal da Meta: Messenger, Direct ou WhatsApp Cloud API.
//
// Dois caminhos, porque na prática existem dois tipos de cliente. Quem tem app
// próprio publicado entra pelo login da Meta e o sistema descobre páginas e
// números sozinho. Quem recebe do cliente um token permanente e um id de
// número — o caso comum na revenda — não tem app para logar: cola as
// credenciais na segunda aba.

const CANAIS = {
	facebook: {
		titulo: "Conectar página do Facebook",
		descricao:
			"As conversas do Messenger da página passam a cair na tela de Atendimentos, com fila e chatbot como qualquer outra conexão.",
		Icone: () => <FacebookIcon className="h-5 w-5 text-[#1877f2]" />,
		canalLogin: "facebook",
		channel: "facebook",
		docs: "https://developers.facebook.com/docs/messenger-platform/",
	},
	instagram: {
		titulo: "Conectar Instagram",
		descricao:
			"O Direct da conta comercial vira atendimento. Quem responde é a Página dona da conta — por isso o que se informa aqui é a página, e não o @.",
		Icone: () => <InstagramIcon className="h-5 w-5 text-[#e1306c]" />,
		canalLogin: "instagram",
		channel: "instagram",
		docs: "https://developers.facebook.com/docs/messenger-platform/instagram/",
	},
	whatsapp_official: {
		titulo: "Conectar WhatsApp Oficial (Cloud API)",
		descricao:
			"Número da API oficial do WhatsApp. Não tem QR Code: quem entrega as mensagens é a Meta, pelo webhook. O número segue atendido pelo celular se a coexistência estiver ligada.",
		Icone: () => <MessageCircle className="h-5 w-5 text-emerald-700" />,
		canalLogin: "whatsapp",
		channel: "whatsapp_official",
		docs: "https://developers.facebook.com/docs/whatsapp/cloud-api/",
	},
};

const MetaConnectModal = ({ open, onClose, canal }) => {
	const info = CANAIS[canal] || CANAIS.facebook;

	const [appId, setAppId] = useState("");
	const [configIds, setConfigIds] = useState(null);
	const [carregando, setCarregando] = useState(false);
	const [aba, setAba] = useState("login");
	const [form, setForm] = useState({
		name: "",
		token: "",
		pageId: "",
		phoneNumberId: "",
		wabaId: "",
	});

	useEffect(() => {
		if (!open) return;

		let ativo = true;

		buscarAppConfigMeta().then((cfg) => {
			if (!ativo) return;

			setAppId(cfg.appId || "");
			setConfigIds(cfg.configIds);
			// Sem app da Meta configurado o login nem aparece: abrir nele deixaria
			// o modal com uma aba vazia e nenhuma saída.
			setAba(cfg.appId ? "login" : "manual");
		});

		return () => {
			ativo = false;
		};
	}, [open]);

	const fechar = useCallback(() => {
		setForm({ name: "", token: "", pageId: "", phoneNumberId: "", wabaId: "" });
		setCarregando(false);
		onClose();
	}, [onClose]);

	const entrarComMeta = async () => {
		setCarregando(true);

		try {
			const conectou = await conectarPelaMeta({
				canal: info.canalLogin,
				appId,
				configIds,
			});

			if (conectou) {
				toast.success("Conta conectada.");
				fechar();
			}
		} catch (err) {
			toastError(err);
		} finally {
			setCarregando(false);
		}
	};

	const salvarManual = async (e) => {
		e.preventDefault();
		setCarregando(true);

		try {
			await api.post("/whatsapp/meta-manual", {
				channel: info.channel,
				...form,
			});

			toast.success("Conta conectada.");
			fechar();
		} catch (err) {
			toastError(err);
		} finally {
			setCarregando(false);
		}
	};

	const campo = (id, rotulo, ajuda, opcional = false) => (
		<div className="space-y-1.5">
			<Label htmlFor={id}>
				{rotulo}
				{opcional && (
					<span className="ml-1 font-normal text-muted-foreground">
						(opcional)
					</span>
				)}
			</Label>
			<Input
				id={id}
				value={form[id]}
				autoComplete="off"
				onChange={(ev) => setForm((p) => ({ ...p, [id]: ev.target.value }))}
			/>
			<p className="text-xs leading-relaxed text-muted-foreground">{ajuda}</p>
		</div>
	);

	const podeSalvar =
		form.token.trim() &&
		(info.channel === "whatsapp_official"
			? form.phoneNumberId.trim() && form.wabaId.trim()
			: form.pageId.trim());

	return (
		<Dialog open={open} onOpenChange={(o) => !o && fechar()}>
			<DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2">
						<info.Icone />
						{info.titulo}
					</DialogTitle>
					<DialogDescription>{info.descricao}</DialogDescription>
				</DialogHeader>

				<Tabs value={aba} onValueChange={setAba}>
					<TabsList className="w-full">
						{appId && (
							<TabsTrigger value="login" className="flex-1">
								Entrar com a Meta
							</TabsTrigger>
						)}
						<TabsTrigger value="manual" className="flex-1">
							Colar credenciais
						</TabsTrigger>
					</TabsList>

					{appId && (
						<TabsContent value="login" className="space-y-4 pt-4">
							<p className="text-sm leading-relaxed text-muted-foreground">
								Abre a janela da Meta para você autorizar o acesso. Ao voltar, o
								sistema cria uma conexão para cada{" "}
								{info.channel === "whatsapp_official"
									? "número encontrado"
									: "página autorizada"}{" "}
								e assina os webhooks — não há nada para copiar à mão.
							</p>
							<Button
								className="w-full"
								disabled={carregando}
								onClick={entrarComMeta}
							>
								{carregando && <Loader2 className="h-4 w-4 animate-spin" />}
								<info.Icone />
								Entrar com a Meta
							</Button>
						</TabsContent>
					)}

					<TabsContent value="manual" className="pt-4">
						<form className="space-y-4" onSubmit={salvarManual}>
							{info.channel === "whatsapp_official" ? (
								<>
									{campo(
										"phoneNumberId",
										"Phone Number ID",
										"O id do número na Cloud API — não é o telefone. Está no painel do app da Meta, em WhatsApp › Configuração da API."
									)}
									{campo(
										"wabaId",
										"WhatsApp Business Account ID (WABA)",
										"A conta comercial dona do número. É nela que o webhook é assinado; sem isso o token funciona para enviar, mas nada chega."
									)}
									{campo(
										"token",
										"Token de acesso permanente",
										"Token do usuário de sistema com whatsapp_business_messaging e whatsapp_business_management. Token temporário do Graph Explorer expira em horas e a conexão para sozinha."
									)}
								</>
							) : (
								<>
									{campo(
										"pageId",
										"ID da página do Facebook",
										info.channel === "instagram"
											? "A página ligada à conta do Instagram. O @ da conta é descoberto a partir dela."
											: "Está em Configurações da página › Informações, no fim da tela."
									)}
									{campo(
										"token",
										"Token da página",
										"Token de acesso da página, não o do usuário. Gerado com um token de usuário longo, ele não expira."
									)}
								</>
							)}

							{campo(
								"name",
								"Nome da conexão",
								"Como ela aparece na lista e na escolha de conexão ao enviar. Em branco, o sistema usa o nome que vier da Meta.",
								true
							)}

							<a
								href={info.docs}
								target="_blank"
								rel="noreferrer"
								className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
							>
								Onde encontrar estes dados
								<ExternalLink className="h-3 w-3" />
							</a>

							<DialogFooter>
								<Button
									type="button"
									variant="outline"
									onClick={fechar}
									disabled={carregando}
								>
									Cancelar
								</Button>
								<Button type="submit" disabled={carregando || !podeSalvar}>
									{carregando && <Loader2 className="h-4 w-4 animate-spin" />}
									Conectar
								</Button>
							</DialogFooter>
						</form>
					</TabsContent>
				</Tabs>
			</DialogContent>
		</Dialog>
	);
};

export default MetaConnectModal;
